import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Skeleton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import AttachMoneyRoundedIcon from "@mui/icons-material/AttachMoneyRounded";
import { LoadingButton } from "@mui/lab";
import axios from "axios";
import { toast } from "react-toastify";
import { getAllFilterContacts } from "../../../redux/Apps/Lawyerapp/ContactSlice/ContactSlice";
import { backend_api } from "../../../static/server";
import {
  getAllBilles,
  updateStatus,
} from "../../../redux/Apps/Lawyerapp/BillingSlice/BillingSlice";
import { getAllAccounts } from "../../../redux/Apps/Lawyerapp/AccountSlice/AccountSlice";
import { generateInvoiceNumber } from "./Billes";
import { renderContactOption } from "../../../static/staticdata";
import { useDialog } from "../../DialogContext";
import { compactGridStyle, pageScrollStyle } from "../../../styles/styles";

const RecordPaymentFrom = ({ entryData }) => {
  const { closeDialog } = useDialog();
  const dispatch = useDispatch();
  const { token } = useSelector((data) => data.user);
  const [totalAmount, setTotalAmount] = useState(0);
  const [showWriteOff, setShowWriteOff] = useState(false);
  const [remainBalance, setRemainBalance] = useState(0);
  const [writeOffAmount, setWriteOffAmount] = useState(0);
  const [clientBills, setClientBills] = useState(null);
  const [lawFirmAccounts, setLawFirmAccounts] = useState([]);
  const [clientFundAccounts, setClientFundAccounts] = useState([]);
  const [fetching, setFetching] = useState(!!entryData);
  const { filteredContacts } = useSelector((state) => state.contact);
  const { AllAccounts, loading } = useSelector((state) => state.account);
  const { allBilles, loading: billLoading } = useSelector(
    (state) => state.billing
  );

  useEffect(() => {
    dispatch(
      getAllFilterContacts({
        url: `${backend_api}contact/retrieve/`,
        token: token,
      })
    );
    dispatch(
      getAllBilles({
        url: `${backend_api}firm-case/bill/?status=unpaid`,
        token,
      })
    );
    const getLawFirmAccounts = async () => {
      const response = await dispatch(
        getAllAccounts({
          url: `${backend_api}bank/accounts/?account_type=law firm`,
          token: token,
        })
      );
      if (response?.payload) setLawFirmAccounts(response?.payload?.results);
    };
    getLawFirmAccounts();
  }, [dispatch, token]);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      date: dayjs().format("YYYY-MM-DD"),
      bill: null,
      case: null,
      expenses: null,
      firm: null,
    },
  });

  useEffect(() => {
    if (entryData) {
      setFetching(true);
      setValue("client", entryData?.row?.full_data?.client?.id);
      setValue("bill", entryData?.row?.full_data?.bill?.id);
      setValue("case", entryData?.row?.full_data?.case?.id);
      setValue("expenses", entryData?.row?.full_data?.expenses);
      setValue("firm", entryData?.row?.full_data?.firm?.id);
      setValue("amount", entryData?.row?.full_data?.total_amount);
      setFetching(false);
    }
  }, [entryData]);

  const client = watch("client");

  useEffect(() => {
    if (client) {
      const getClientFundAccounts = async () => {
        const response = await dispatch(
          getAllAccounts({
            url: `${backend_api}bank/accounts/?client=${client}&account_type=client fund`,
            token: token,
          })
        );
        if (response?.payload)
          setClientFundAccounts(response?.payload?.results);
      };
      getClientFundAccounts();
      if (allBilles && allBilles?.length > 0) {
        const filteredBills = allBilles?.filter(
          (bill) => bill?.client?.id === client
        );
        setClientBills(filteredBills);
      } else {
        setClientBills([]);
      }
      dispatch(
        getAllFilterContacts({
          url: `${backend_api}contact/retrieve/?contact_name=${entryData?.row?.full_data?.client?.first_name}`,
          token: token,
        })
      );
    } else {
      setClientFundAccounts([]);
      setClientBills([]);
    }
  }, [dispatch, token, client, allBilles]);

  const paymentSourceOptions = useMemo(
    () => [
      { label: "Direct Payment", value: "direct_payment" },
      ...clientFundAccounts?.map((acc) => ({
        label: acc.account_name,
        value: acc.id,
      })),
    ],
    [clientFundAccounts]
  );

  const amount = watch("amount");
  const write_off = watch("write_off");

  useEffect(() => {
    if (amount) {
      const numericPaymentAmount = parseFloat(amount);
      if (!isNaN(numericPaymentAmount)) {
        const calculatedBalance = totalAmount - numericPaymentAmount;
        const roundedBalance = Math.round(calculatedBalance * 100) / 100;
        setRemainBalance(roundedBalance);
        if (roundedBalance > 0) {
          setWriteOffAmount(roundedBalance);
        }
        setShowWriteOff(roundedBalance > 0);
        if (roundedBalance <= 0) {
          setValue("write_off", false);
          setWriteOffAmount(0);
        }
      }
    } else {
      setRemainBalance(totalAmount);
      setShowWriteOff(false);
      setValue("write_off", false);
      setWriteOffAmount(0);
    }
  }, [amount, totalAmount, setValue]);

  // Update remaining balance based on write-off toggle
  useEffect(() => {
    if (write_off) {
      setRemainBalance(0);
    } else if (amount) {
      const numericPaymentAmount = parseFloat(amount);
      if (!isNaN(numericPaymentAmount)) {
        const calculatedBalance = totalAmount - numericPaymentAmount;
        setRemainBalance(Math.round(calculatedBalance * 100) / 100);
      }
    }
  }, [write_off, amount, totalAmount]);

  const onSubmit = async (data) => {
    const updateBillData = {
      case: data?.case,
      expenses: data?.expenses?.map((exp) => exp?.id) || [],
      status: "paid",
      firm: data?.firm,
    };

    const destinationDepositData = {
      transaction_type: "deposit",
      account: data?.destination_account,
      date: data?.date,
      client: data?.client,
      matter: data?.case,
      destination_account: data?.destination_account,
      payer_payee: data?.payer_payee,
      currency: "USD",
      amount: data?.amount,
      description: data?.description,
      status: "completed",
      ...(data?.source_account
        ? { source_account: data?.source_account }
        : { source_destination: data?.source_destination }),
    };

    const sourceWithdrawData = data?.source_account
      ? {
          transaction_type: "withdrawal",
          account: data?.source_account,
          date: data?.date,
          client: data?.client,
          matter: data?.case,
          source_account: data?.source_account,
          destination_account: data?.destination_account,
          payer_payee: data?.payer_payee,
          currency: "USD",
          amount: data?.amount,
          description: data?.description,
          status: "completed",
        }
      : null;

    // console.log(
    //   "Destination Deposit Data:",
    //   data,
    //   destinationDepositData,
    //   sourceWithdrawData
    // );

    try {
      await dispatch(
        updateStatus({ id: data?.bill, data: updateBillData, token: token })
      );
      const apiCalls = [
        axios.post(`${backend_api}bank/transactions/`, destinationDepositData, {
          headers: { Authorization: `Token ${token}` },
        }),
      ];

      if (sourceWithdrawData) {
        apiCalls.push(
          axios.post(`${backend_api}bank/transactions/`, sourceWithdrawData, {
            headers: { Authorization: `Token ${token}` },
          })
        );
      }

      const responses = await Promise.all(apiCalls);
      // console.log("API responses:", responses);
      toast.success("Payment recorded successfully!");
    } catch (error) {
      console.error("Error occurred:", error);
      try {
        await dispatch(
          updateStatus({
            id: data?.bill,
            data: { status: "pending" },
            token: token,
          })
        );
      } catch (rollbackError) {
        console.error("Rollback failed:", rollbackError);
      }
      toast.error("Transaction failed! Payment not recorded.");
    } finally {
      closeDialog();
      reset();
      setClientBills([]);
    }
  };
  // Table columns configuration
  const columns = [
    { field: "account", headerName: "Account", flex: 1 },
    { field: "type", headerName: "Type", flex: 1 },
    {
      field: "amount",
      headerName: "Amount",
      flex: 1,
    },
  ];

  const summaryCol = [
    { field: "invoice_id", headerName: "Invoice ID", flex: 1 },
    { field: "matter", headerName: "Matter(s)", flex: 1 },
    {
      field: "issue_date",
      headerName: "Issue date",
      flex: 1,
    },
    {
      field: "due_in",
      headerName: "Due in",
      flex: 1,
    },
    {
      field: "due_data",
      headerName: "Due Date",
      flex: 1,
    },
    {
      field: "interest",
      headerName: "Interest",
      flex: 1,
    },
    {
      field: "open_balance",
      headerName: "Open Balance",
      flex: 1,
    },
    {
      field: "payment",
      headerName: "Payment",
      flex: 1,
    },
  ];

  // Create rows for the accounts table
  const rows = useMemo(() => {
    if (!Array.isArray(AllAccounts) || AllAccounts.length === 0) return [];

    return AllAccounts?.map((item) => {
      return {
        id: item?.id ?? "-",
        account: item?.account_name ?? "-",
        type: item?.account_type ?? "-",
        amount: item?.balance ?? "-",
      };
    });
  }, [AllAccounts]);

  // Create rows for the bills summary table
  const watchedAmount = watch("amount");
  const selectedBillId = watch("bill");

  const summaryRows = useMemo(() => {
    if (!Array.isArray(clientBills) || clientBills.length === 0) {
      return [];
    }

    return clientBills?.map((item) => {
      return {
        id: item?.id ?? "-",
        invoice_id: item?.id ? generateInvoiceNumber(item?.id) : "-",
        matter: item?.case?.case_name ?? "-",
        issue_date: item?.issue_date
          ? dayjs(item.issue_date).format("MM/DD/YYYY")
          : "-",
        due_data: item?.due_date
          ? dayjs(item.due_date).format("MM/DD/YYYY")
          : "-",
        interest: item?.interest_rate ? `${item.interest_rate}%` : "-",
        open_balance: item?.total_amount ?? "-",
        payment: item?.id === selectedBillId ? watchedAmount ?? "-" : "-",
      };
    });
  }, [clientBills, selectedBillId, watchedAmount]);

  useEffect(() => {
    if (!Array.isArray(clientBills) || clientBills.length === 0) {
      setTotalAmount(0);
      return;
    }

    const selected = clientBills.find((b) => b.id === selectedBillId);
    if (selected) {
      setTotalAmount(selected.total_amount ?? 0);
    } else {
      setTotalAmount(0);
    }
  }, [clientBills, selectedBillId]);

  // Get selected client name for display
  const selectedClientName = useMemo(() => {
    if (!client) return "";
    const selectedContact = filteredContacts?.find((c) => c.id === client);
    if (!selectedContact) return "";

    return selectedContact.first_name
      ? `${selectedContact.first_name} ${selectedContact.last_name}`
      : selectedContact.company_name;
  }, [client, filteredContacts]);

  return (
    <>
      <Box>
        {fetching ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              p: "1rem",
              width: "100%",
            }}
          >
            <Box sx={{ display: "flex", gap: "1rem" }}>
              <Skeleton variant="rounded" width="100%" height="2.5rem" />
              <Skeleton variant="rounded" width="100%" height="2.5rem" />
            </Box>
            <Box sx={{ display: "flex", gap: "1rem" }}>
              <Skeleton variant="rounded" width="100%" height="2.5rem" />
              <Skeleton variant="rounded" width="100%" height="2.5rem" />
            </Box>
            <Skeleton variant="rounded" width="100%" height="2.5rem" />
            <Skeleton variant="rounded" width="100%" height="2.5rem" />
            <Skeleton variant="rounded" width="100%" height="2.5rem" />
            <Skeleton variant="rounded" width="100%" height="2.5rem" />
            <Skeleton variant="rounded" width="100%" height="2.5rem" />
          </Box>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                p: "1rem",
              }}
            >
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <Controller
                  name="client"
                  control={control}
                  rules={{ required: "Client is required" }}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <Autocomplete
                        disablePortal
                        options={filteredContacts || []}
                        getOptionLabel={(option) =>
                          option?.first_name
                            ? `${option?.first_name} ${option?.last_name}`
                            : option?.company_name || ""
                        }
                        value={
                          filteredContacts?.find((c) => c.id === field.value) ||
                          null
                        }
                        onChange={(_, newValue) => {
                          console.log("Selected Client:", newValue);
                          field.onChange(newValue ? newValue.id : null);
                        }}
                        isOptionEqualToValue={(option, value) =>
                          option?.id === value?.id
                        }
                        noOptionsText="No contact found"
                        renderOption={renderContactOption}
                        renderInput={(params) => {
                          const { InputProps, ...rest } = params;
                          return (
                            <TextField
                              {...rest}
                              label={
                                <span>
                                  Client<span style={{ color: "red" }}>*</span>
                                </span>
                              }
                              error={!!errors.client}
                              helperText={errors.client?.message}
                              size="small"
                              InputProps={{
                                ...InputProps,
                                startAdornment: field.value ? (
                                  <Chip
                                    size="small"
                                    key={field.value}
                                    sx={{
                                      bgcolor:
                                        filteredContacts?.find(
                                          (c) => c.id === field.value
                                        )?.contact_type === "person"
                                          ? "#bf3fe1"
                                          : "#2e77f5",
                                      color: "#fff",
                                    }}
                                    label={
                                      <Typography variant="body1">
                                        {filteredContacts?.find(
                                          (c) => c.id === field.value
                                        )?.contact_type === "person" ? (
                                          <AccountCircleRoundedIcon
                                            sx={{
                                              color: "#fff",
                                              fontSize: "0.9rem",
                                            }}
                                          />
                                        ) : (
                                          <BusinessRoundedIcon
                                            sx={{
                                              color: "#fff",
                                              fontSize: "0.9rem",
                                            }}
                                          />
                                        )}
                                      </Typography>
                                    }
                                  />
                                ) : null,
                              }}
                            />
                          );
                        }}
                      />
                    </FormControl>
                  )}
                />
                <Controller
                  name="source_account"
                  control={control}
                  rules={{
                    required: {
                      message: "Payment Source is required",
                    },
                  }}
                  render={({ field }) => {
                    const currentValue = watch();
                    return (
                      <FormControl fullWidth>
                        <Autocomplete
                          disablePortal
                          disabled={!client}
                          options={paymentSourceOptions}
                          getOptionLabel={(option) => option.label}
                          value={
                            paymentSourceOptions?.find(
                              (opt) =>
                                (currentValue?.source_destination ===
                                  "direct_payment" &&
                                  opt?.value === "direct_payment") ||
                                opt?.value === currentValue?.source_account
                            ) || null
                          }
                          isOptionEqualToValue={(option, value) =>
                            option?.value === value?.value
                          }
                          onChange={(_, selectedOption) => {
                            if (selectedOption?.value === "direct_payment") {
                              setValue("source_destination", "direct_payment");
                              setValue("source_account", null);
                            } else if (selectedOption) {
                              setValue("source_account", selectedOption.value);
                              setValue("source_destination", null);
                            } else {
                              setValue("source_account", null);
                              setValue("source_destination", null);
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={
                                <span>
                                  Payment Source
                                  <span style={{ color: "red" }}>*</span>
                                </span>
                              }
                              size="small"
                              error={!!errors.source_account}
                              helperText={errors.source_account?.message}
                            />
                          )}
                        />
                      </FormControl>
                    );
                  }}
                />
              </Box>
              {client &&
                Array.isArray(AllAccounts) &&
                AllAccounts.length > 0 && (
                  <Box>
                    <Accordion
                      defaultExpanded
                      sx={{
                        p: "0rem",
                        boxShadow: "none",
                        border: "1px solid #e5e5e5",
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <KeyboardArrowDownRoundedIcon
                            sx={{ fontSize: "1rem", margin: 0 }}
                          />
                        }
                        sx={{
                          padding: "0rem 0.5rem",
                          minHeight: "1.5rem !important",
                          height: "1.5rem",
                          "& .MuiAccordionSummary-content": {
                            margin: 0,
                            minHeight: "1.5rem",
                          },
                        }}
                      >
                        <Typography
                          component="span"
                          sx={{ fontSize: "0.8rem", fontWeight: 600 }}
                        >
                          Available Trust Fund for {selectedClientName}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ padding: "0rem 0rem" }}>
                        <DataGrid
                          rows={rows}
                          columns={columns}
                          loading={loading}
                          pageSize={5}
                          autoHeight
                          slotProps={{
                            loadingOverlay: {
                              variant: "skeleton",
                              noRowsVariant: "skeleton",
                              rowCount: 5,
                            },
                          }}
                          hideFooter={true}
                          initialState={{
                            pinnedColumns: {
                              left: ["desk"],
                            },
                            pagination: {
                              paginationModel: { pageSize: 5 },
                            },
                          }}
                          disableRowSelectionOnClick
                          disableSelectionOnClick
                          sx={compactGridStyle}
                        />
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                )}
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <Controller
                  name="date"
                  control={control}
                  rules={{ required: "Payment date is required" }}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      disabled={!client}
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(newValue) => {
                        const isValidDate =
                          newValue && dayjs(newValue).isValid();
                        field.onChange(
                          isValidDate
                            ? dayjs(newValue)
                                .startOf("day")
                                .format("YYYY-MM-DD")
                            : null
                        );
                      }}
                      sx={{
                        width: "100%",
                      }}
                      slotProps={{
                        textField: {
                          label: (
                            <span>
                              Payment Date
                              <span style={{ color: "red" }}>*</span>
                            </span>
                          ),
                          size: "small",
                          variant: "outlined",
                        },
                        popper: {
                          sx: {
                            "& .MuiPickersPopper-root": {
                              maxWidth: "100%",
                              maxHeight: "100%",
                              overflow: "hidden",
                            },
                            "& .MuiPaper-root": {
                              width: "fit-content",
                              maxHeight: "15rem",
                              overflowY: "auto",
                              ...pageScrollStyle,
                            },
                          },
                        },
                      }}
                      format="MM/DD/YYYY"
                    />
                  )}
                />
                <Controller
                  name="destination_account"
                  control={control}
                  rules={{ required: "Destination Account is required" }}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <Autocomplete
                        disablePortal
                        disabled={!client}
                        options={lawFirmAccounts || []}
                        getOptionLabel={(option) => option?.account_name || ""}
                        value={
                          Array.isArray(lawFirmAccounts)
                            ? lawFirmAccounts?.find(
                                (acc) => acc.id === field.value
                              ) || null
                            : null
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.id === value?.id
                        }
                        onChange={(_, selectedAccount) =>
                          field.onChange(
                            selectedAccount ? selectedAccount.id : null
                          )
                        }
                        renderOption={(props, option) => (
                          <li
                            {...props}
                            key={option.id}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography variant="body1">
                              {option.account_name}
                            </Typography>
                            <Box>
                              {option.is_default && (
                                <Chip
                                  label="Default"
                                  size="small"
                                  color="primary"
                                />
                              )}
                            </Box>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={
                              <span>
                                Destination Account
                                <span style={{ color: "red" }}>*</span>
                              </span>
                            }
                            size="small"
                          />
                        )}
                      />
                    </FormControl>
                  )}
                />
              </Box>
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <Controller
                  name="reference"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Reference Id"
                      size="small"
                      fullWidth
                    />
                  )}
                />
                <Controller
                  name="payer_payee"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Payer"
                      size="small"
                      fullWidth
                    />
                  )}
                />
              </Box>
              {client && clientBills && clientBills.length > 0 && (
                <Box>
                  <Accordion
                    defaultExpanded
                    sx={{
                      padding: "0rem",
                      boxShadow: "none",
                      border: "1px solid #e5e5e5",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <KeyboardArrowDownRoundedIcon
                          sx={{ fontSize: "1rem", margin: 0 }}
                        />
                      }
                      sx={{
                        padding: "0rem 0.5rem",
                        minHeight: "1.5rem !important",
                        height: "1.5rem",
                        "& .MuiAccordionSummary-content": {
                          margin: 0,
                          minHeight: "1.5rem",
                        },
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{ fontSize: "0.8rem", fontWeight: 600 }}
                      >
                        Summary
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ padding: "0rem 0rem" }}>
                      <DataGrid
                        rows={summaryRows}
                        columns={summaryCol}
                        loading={billLoading}
                        pageSize={5}
                        autoHeight
                        slotProps={{
                          loadingOverlay: {
                            variant: "skeleton",
                            noRowsVariant: "skeleton",
                            rowCount: 5,
                          },
                        }}
                        hideFooter={true}
                        initialState={{
                          pinnedColumns: {
                            left: ["desk"],
                          },
                          pagination: {
                            paginationModel: { pageSize: 5 },
                          },
                        }}
                        getRowClassName={(params) =>
                          params.id === selectedBillId ? "selected-bill" : ""
                        }
                        disableRowSelectionOnClick
                        disableSelectionOnClick
                        sx={{
                          ...compactGridStyle,
                          "& .selected-bill": {
                            bgcolor: "#fff7e0",
                          },
                        }}
                      />
                    </AccordionDetails>
                  </Accordion>
                </Box>
              )}

              <Box>
                <Box
                  sx={{
                    border: "1px solid #0000003B",
                    borderRadius: "0.3rem 0.3rem 0rem 0.3rem",
                    width: "100%",
                    p: "0.5rem",
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{
                        fontSize: "0.9rem",
                        fontWeight: "500",
                      }}
                    >
                      Total open balance:
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: "1.2rem",
                        fontWeight: "600",
                        textAlign: "end",
                        pl: 2,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      $ {Number(totalAmount).toFixed(2)}
                    </Typography>
                  </Box>

                  <Divider sx={{ width: "100%" }} />

                  {/* Payment Amount Input Section */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "1rem",
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{
                        fontSize: "0.9rem",
                        fontWeight: "500",
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                        minWidth: "fit-content",
                      }}
                    >
                      Payment amount:
                    </Typography>
                    <Controller
                      name="amount"
                      control={control}
                      rules={{
                        max: totalAmount,
                        required: "Payment amount is required",
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          size="small"
                          error={!!errors?.amount}
                          helperText={
                            errors?.amount
                              ? "Amount cannot exceed total amount"
                              : errors?.amount?.message
                          }
                          sx={{
                            minWidth: "180px",
                            maxWidth: "250px",
                            flex: "1 1 auto",
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <AttachMoneyRoundedIcon />
                              </InputAdornment>
                            ),
                            inputProps: {
                              min: 0,
                              max: totalAmount,
                              step: 0.01,
                              style: {
                                textAlign: "right",
                                paddingRight: "0.5rem",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Box>
                </Box>

                {showWriteOff && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Controller
                        name="write_off"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <Checkbox {...field} checked={field.value} />
                            }
                            label="Write off remaining balance from invoice Id"
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: "0.8rem",
                                fontWeight: 500,
                                color: "text.secondary",
                              },
                            }}
                          />
                        )}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.5rem",
                        borderLeft: "1px solid #0000003B",
                        borderRight: "1px solid #0000003B",
                        borderBottom: "1px solid #0000003B",
                        p: "0.5rem",
                        borderRadius: "0rem 0rem 0.3rem 0.3rem",
                        minWidth: "fit-content",
                      }}
                    >
                      {write_off && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "1.5rem",
                          }}
                        >
                          <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{
                              fontSize: "0.9rem",
                              fontWeight: "500",
                              flexShrink: 0,
                              whiteSpace: "nowrap",
                            }}
                          >
                            Credit balance :
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontSize: "1.2rem",
                              fontWeight: "600",
                              textAlign: "end",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              minWidth: "100px",
                            }}
                          >
                            ${" "}
                            {writeOffAmount.toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </Typography>
                        </Box>
                      )}
                      {write_off && <Divider sx={{ width: "100%" }} />}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: "1.5rem",
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          sx={{
                            fontSize: "0.9rem",
                            fontWeight: "500",
                            flexShrink: 0,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Remaining balance :
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontSize: "1.2rem",
                            fontWeight: "600",
                            textAlign: "end",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            minWidth: "100px",
                          }}
                        >
                          ${" "}
                          {remainBalance.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
              <Controller
                name="description"
                control={control}
                render={({ field }) => {
                  const autoDescription =
                    client && clientBills?.length > 0
                      ? `Payment for bill ${
                          generateInvoiceNumber(watch("bill")) || ""
                        }`
                      : "";

                  if (!field.value && autoDescription) {
                    field.onChange(autoDescription);
                  }

                  return (
                    <TextField
                      {...field}
                      multiline
                      rows={2}
                      fullWidth
                      label="Description"
                      placeholder={
                        autoDescription
                          ? `${autoDescription} (you can modify this)`
                          : "Enter description"
                      }
                      onChange={(e) => {
                        field.onChange(e.target.value);
                      }}
                      InputLabelProps={{
                        shrink: Boolean(field.value),
                      }}
                    />
                  );
                }}
              />
            </Box>
            <Box
              sx={{
                height: "fit-content",
                borderTop: "1px solid #ddd",
                position: "sticky",
                bottom: 0,
                backdropFilter: "blur(10px)",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                p: "0.5rem",
                zIndex: 1000,
                gap: "0.5rem",
              }}
            >
              <Button
                size="small"
                variant="outlined"
                onClick={() => closeDialog()}
              >
                Cancel
              </Button>
              <LoadingButton
                size="small"
                variant="contained"
                color="primary"
                type="submit"
              >
                Record Payment
              </LoadingButton>
            </Box>
          </form>
        )}
      </Box>
    </>
  );
};

export default RecordPaymentFrom;
